/* eslint-disable global-require */
import VuexyLogo from "@core/layouts/components/Logo.vue";
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BFormCheckbox,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BAlert,
  VBTooltip,
} from "bootstrap-vue";
import { required, email } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import useJwt from "@/auth/jwt/useJwt";
import axiosIns from "@/libs/axios";
import store from "@/store/index";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import pages from "@/router/routes/pages";
import others from "@/router/routes/others";
export default {
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BAlert,
    others,
    pages,
    VuexyLogo,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      status: "",
      password: "",
      userEmail: "",
      sideImg: require("@/assets/images/pages/login_page.png"),
      logoImg: require("@/assets/images/pages/Poultrac_logo.png"),
      // validation rules
      required,
      email,
      rowData: [],
      // Super Admin second-factor SMS OTP step
      otpStep: false,
      otpCode: "",
      otpError: "",
      otpVerifying: false,
      otpSessionToken: null,
      resendCooldown: 0,
      resendTimer: null,
      // loginImage: {
      //   backgroundImage: require("@/assets/images/login/login.png"),
      // },
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === "dark") {
        this.sideImg = require("@/assets/images/login/login-left.png");
        return this.sideImg;
      }
      return this.sideImg;
    },
  },
  methods: {
    login() {
      this.$refs.loginForm.validate().then((success) => {
        if (success) {
          useJwt
            .login({
              email: this.userEmail,
              password: this.password,
            })
            .then((response) => {
              const data = response.data.data;

              // Super Admin: password checked out, but the real Sanctum
              // token isn't issued yet — an SMS code has to clear first
              // (see AuthOtpController::verify). Every other role's
              // response already has a real token/user and skips this.
              if (data.otp_required) {
                this.otpSessionToken = data.otp_session_token;
                this.otpStep = true;
                this.otpCode = "";
                this.otpError = "";
                this.startResendCooldown(45);
                return;
              }

              this.completeLogin(data);
            })
            .catch((error) => {
              // Was hardcoded to "Email password did not match" for ANY
              // error.response — a 404 from a broken/stale endpoint, a 500,
              // a validation error, all looked identical to actually wrong
              // credentials. Surface the real backend message when there is
              // one; only fall back to the credentials-guess for the classic
              // 401/422 case where the backend genuinely didn't send one.
              const status = error.response?.status;
              const backendMessage = error.response?.data?.message;
              const text = backendMessage
                || (status === 401 || status === 422
                  ? "Email password did not match"
                  : error.response
                    ? `Login failed (${status || "unknown error"}). Please try again or contact support.`
                    : "Unable to reach server. Please check your connection.");
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  // title: `Welcome `,
                  icon: "CoffeeIcon",
                  variant: "danger",
                  text,
                },
              });
            });
        }
      });
    },

    // Shared by both the direct-login success path and the post-OTP
    // success path — both endpoints return the identical {token, user} shape.
    completeLogin(data) {
      useJwt.setToken(data.token);
      useJwt.setRefreshToken(data.token);
      const userData = data.user;
      const rolesArr = data.user.roles || [];
      const role = Array.isArray(rolesArr) && rolesArr.length > 0
        ? (typeof rolesArr[0] === 'object' ? rolesArr[0].name : rolesArr[0])
        : 'admin';
      userData.role = role;
      sessionStorage.setItem("user-role", role);

      // Company-level module gating (nav visibility) — fetched here so
      // it's already in userData by the time the app boots fresh after
      // the redirect below. Fails open (undefined = "show everything")
      // on any error, same reasoning as the mobile app's fetch: a
      // network hiccup should never hide a feature the company
      // actually has.
      axiosIns
        .get("company/enabled-modules")
        .then((res) => {
          userData.enabledModules = res.data?.data?.enabled_modules ?? undefined;
        })
        .catch(() => {
          userData.enabledModules = undefined;
        })
        .finally(() => {
          sessionStorage.setItem("userData", JSON.stringify(userData));
          window.location.href = "/";
        });
    },

    verifyLoginOtp() {
      this.otpVerifying = true;
      this.otpError = "";
      axiosIns
        .post("verify-login-otp", {
          otp_session_token: this.otpSessionToken,
          otp: this.otpCode,
        })
        .then((response) => {
          this.completeLogin(response.data.data);
        })
        .catch((error) => {
          this.otpError = error.response?.data?.message || "Verification failed. Please try again.";
        })
        .finally(() => {
          this.otpVerifying = false;
        });
    },

    resendLoginOtp() {
      axiosIns
        .post("resend-login-otp", { otp_session_token: this.otpSessionToken })
        .then((response) => {
          this.otpSessionToken = response.data.data.otp_session_token;
          this.otpError = "";
          this.startResendCooldown(response.data.data.resend_cooldown_seconds || 45);
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: { icon: "CoffeeIcon", variant: "success", text: "A new code has been sent." },
          });
        })
        .catch((error) => {
          this.otpError = error.response?.data?.message || "Failed to resend code. Please log in again.";
        });
    },

    startResendCooldown(seconds) {
      this.resendCooldown = seconds;
      clearInterval(this.resendTimer);
      this.resendTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) clearInterval(this.resendTimer);
      }, 1000);
    },

    backToLogin() {
      this.otpStep = false;
      this.otpCode = "";
      this.otpError = "";
      this.otpSessionToken = null;
      clearInterval(this.resendTimer);
    },

    privacy() {
      this.$router.push({
        path: "/privacy",
      });
    },
  },
  beforeDestroy() {
    clearInterval(this.resendTimer);
  },
};
